import type { BoxProps } from '@chakra-ui/react'
import { Box } from '@chakra-ui/react'
import type { ReactNode } from 'react'

/**
 * The line icons the sidebar navigates with.
 *
 * Drawn here rather than taken from a library: there are a handful, they are
 * all one weight of one stroke, and a dependency that ships a thousand to use
 * six is a dependency whose version has to be tracked forever. Inline also
 * means they inherit `currentColor`, which is what lets the sidebar tint them
 * without knowing what they are.
 *
 * One geometry for all of them — a 24-unit box, 1.75 stroke, round caps — so
 * they sit at the same optical weight beside each other. An icon drawn to a
 * different grid reads as a different family, which is most of what makes a
 * hand-assembled set look hand-assembled.
 *
 * The size is adjustable and the grid is not. A glyph rendered smaller scales
 * its stroke with it, which is what an icon at two sizes is supposed to do; a
 * glyph redrawn on a 16-unit grid to be small is a second family, which is the
 * failure above. Every caller that wanted a smaller chevron and drew its own is
 * how this set came to have the same arrow twice.
 */
function Glyph({ children, size = '18' }: { children: ReactNode; size?: string | undefined }) {
  return (
    <Box asChild flexShrink="0">
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {children}
      </svg>
    </Box>
  )
}

/** A conversation. */
export function ChatIcon() {
  return (
    <Glyph>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </Glyph>
  )
}

/** A thing that is sold. */
export function ProductIcon() {
  return (
    <Glyph>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </Glyph>
  )
}

/** A crawl: what a seller publishes, fetched. */
export function CrawlIcon() {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
    </Glyph>
  )
}

/**
 * A disclosure: points right when shut, and is turned down when open.
 *
 * Rotated by whatever holds it rather than swapped for a second glyph, so the
 * two states are one shape and the change between them can be animated.
 */
export function DisclosureIcon({ size }: { size?: string | undefined }) {
  return (
    <Glyph size={size}>
      <path d="m9 6 6 6-6 6" />
    </Glyph>
  )
}

/** A market: the set of things compared against each other. */
export function MarketIcon() {
  return (
    <Glyph>
      <path d="M3 3h7v7H3z" />
      <path d="M14 3h7v7h-7z" />
      <path d="M14 14h7v7h-7z" />
      <path d="M3 14h7v7H3z" />
    </Glyph>
  )
}

/**
 * Scout's own mark. The wordmark wears it, and so does anything Scout is
 * speaking through — an introduction, a section it wrote.
 *
 * The one glyph in the set that is filled rather than stroked, and so the one
 * that does not come off `Glyph`: it is a solid shape on a 20-unit box, and
 * putting a 1.75 stroke on it would make it a different mark rather than the
 * same mark at a different weight. It was drawn twice before this existed,
 * once in the sidebar and once in the conversation, which is the argument for
 * it being here.
 */
export function SparkleIcon({ boxSize = '3.5', ...rest }: BoxProps) {
  return (
    <Box asChild flexShrink="0" boxSize={boxSize} {...rest}>
      <svg viewBox="0 0 20 20" aria-hidden="true" fill="currentColor">
        <path d="M10 0 L12.4 7.6 L20 10 L12.4 12.4 L10 20 L7.6 12.4 L0 10 L7.6 7.6 Z" />
      </svg>
    </Box>
  )
}

/** A brief: what a buyer wants, written down. */
export function BriefIcon() {
  return (
    <Glyph>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6" />
      <path d="M9 17h4" />
    </Glyph>
  )
}

/** Adding one of whatever is being listed. */
export function PlusIcon() {
  return (
    <Glyph>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </Glyph>
  )
}

/*
 * The five kinds a criterion can be filed under, one mark each.
 *
 * They mark a section rather than decorate it: the brief panel draws all five
 * whether or not the buyer has said anything about them, so the mark is how a
 * section is told apart at a glance from the four others that look exactly
 * like it.
 */

/** `capability` — what the thing being bought has to do. */
export function RequirementsIcon() {
  return (
    <Glyph>
      <path d="M10 6h10" />
      <path d="M10 12h10" />
      <path d="M10 18h10" />
      <path d="m3.5 5.5 1.5 1.5 2.5-3" />
      <path d="m3.5 11.5 1.5 1.5 2.5-3" />
      <path d="m3.5 17.5 1.5 1.5 2.5-3" />
    </Glyph>
  )
}

/** `company` — the shape of the buyer. */
export function CompanyIcon() {
  return (
    <Glyph>
      <path d="M3 21h18" />
      <path d="M5 21V8l7-4 7 4v13" />
      <path d="M10 21v-5h4v5" />
    </Glyph>
  )
}

/** `budget` — what can be spent, and by when. */
export function BudgetIcon() {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </Glyph>
  )
}

/** `stack` — what the buyer already runs on, and who they already work with. */
export function PreferencesIcon() {
  return (
    <Glyph>
      <path d="M12 20.5 4.8 13.3a4.5 4.5 0 0 1 6.4-6.35l.8.8.8-.8a4.5 4.5 0 0 1 6.4 6.35z" />
    </Glyph>
  )
}

/** `other` — anything that fits none of the above. */
export function OtherIcon() {
  return (
    <Glyph>
      <path d="M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9z" />
      <path d="M7.5 7.5h.01" />
    </Glyph>
  )
}

/** Something that will not ring again: a notice that an agreement ends. */
export function BellOffIcon() {
  return (
    <Glyph>
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
      <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
      <path d="M18 8a6 6 0 0 0-9.33-5" />
      <path d="m1 1 22 22" />
    </Glyph>
  )
}

/** Going round again: a renewal. */
export function RenewIcon() {
  return (
    <Glyph>
      <path d="M23 4v6h-6" />
      <path d="M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </Glyph>
  )
}

/** Looking for something. */
export function SearchIcon() {
  return (
    <Glyph>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </Glyph>
  )
}
